/**
 * 게임별 메뉴 활성화/비활성화 설정
 * 회사코드별로 설정을 관리할 수 있습니다.
 * 
 * 구조:
 * - default.js: 기본 메뉴 설정
 * - companies/: 회사코드별 설정 파일 디렉토리
 *   - index.js: 모든 회사코드 설정을 모아서 export
 *   - 1000.js, 2000.js 등: 각 회사코드별 설정 파일 (4자리 숫자)
 * 
 * 회사코드는 4자리 숫자(0000~9999)로 구성됩니다.
 */

import { DEFAULT_MENU_CONFIG } from './default';
import { COMPANY_CONFIGS } from './companies';

/**
 * 현재 사용자의 회사코드 가져오기
 * @returns {string|null} 회사코드 (4자리 숫자 문자열, 예: '1000', '2000')
 */
const getCurrentCompanyCode = () => {
  try {
    const userItem = localStorage.getItem('user');
    if (!userItem) return null;
    
    const parsedItem = JSON.parse(userItem);
    if (parsedItem.expiry && new Date().getTime() > parsedItem.expiry) {
      return null;
    }
    
    const companyCode = parsedItem.value?.companyCode;
    // 회사코드를 4자리 문자열로 정규화 (예: 1000 -> '1000', '0100' -> '0100')
    if (companyCode) {
      // 숫자면 4자리 문자열로 변환, 이미 문자열이면 그대로 사용
      const codeStr = String(companyCode);
      // 4자리로 패딩 (앞에 0 추가)
      return codeStr.padStart(4, '0');
    }
    
    return null;
  } catch (error) {
    console.error('회사코드 가져오기 오류:', error);
    return null;
  }
};

/**
 * 깊은 병합 함수
 */
const deepMerge = (target, source) => {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  
  return output;
};

const isObject = (item) => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * 회사코드에 해당하는 메뉴 설정 가져오기
 * @param {string|number} companyCode - 회사코드 (없으면 현재 사용자의 회사코드 사용)
 * @returns {Object} 메뉴 설정 객체
 */
const getMenuConfig = (companyCode = null) => {
  let code = companyCode;
  
  // 회사코드가 제공되지 않으면 현재 사용자의 회사코드 사용
  if (!code) {
    code = getCurrentCompanyCode();
  } else {
    // 제공된 회사코드를 4자리 문자열로 정규화
    code = String(code).padStart(4, '0');
  }
  
  // 회사코드가 있고 해당 설정이 있으면 사용
  if (code && COMPANY_CONFIGS[code]) {
    // 기본 설정과 회사코드별 설정을 병합
    return deepMerge(DEFAULT_MENU_CONFIG, COMPANY_CONFIGS[code]);
  }
  
  // 기본 설정 반환
  return DEFAULT_MENU_CONFIG;
};

// 현재 사용자의 회사코드에 맞는 설정 (하위 호환성을 위해 export)
// 주의: 이 값은 모듈 로드 시점에 계산되므로, 로그인 전에는 기본 설정을 반환합니다.
// 실제 메뉴 확인은 isGameEnabled(), isMenuEnabled() 함수를 사용하세요.
export const MENU_CONFIG = getMenuConfig();

// 디버깅용: 현재 회사코드와 설정 확인 함수
export const getCurrentMenuConfig = () => {
  const code = getCurrentCompanyCode();
  const config = getMenuConfig();
  console.log('현재 회사코드:', code);
  console.log('사용 중인 설정:', config);
  console.log('등록된 회사코드 목록:', Object.keys(COMPANY_CONFIGS));
  return { code, config };
};

// 메뉴 활성화 상태 확인 함수
export const isMenuEnabled = (category, subcategory = null, item = null, companyCode = null) => {
  const config = getMenuConfig(companyCode);
  
  if (!config.menuItems[category]) return false;
  
  if (subcategory && item) {
    return config.menuItems[category][subcategory]?.[item] || false;
  }
  
  if (subcategory) {
    return config.menuItems[category][subcategory] || false;
  }
  
  return config.menuItems[category] || false;
};

/**
 * 관리타입 (.env REACT_APP_MANAGE_TYPE)
 * 1: 스포츠 / 미니게임 / 카지노 / 슬롯
 * 2: 카지노 / 슬롯
 */
export const MANAGE_TYPE = String(process.env.REACT_APP_MANAGE_TYPE || '1');
const MANAGE_TYPE_GAMES = {
  '1': ['sports', 'minigame', 'casino', 'slot'],
  '2': ['casino', 'slot'],
};

export const isManageGameAllowed = (gameType) => {
  const allowed = MANAGE_TYPE_GAMES[MANAGE_TYPE] || MANAGE_TYPE_GAMES['1'];
  return allowed.includes(gameType);
};

// 게임 활성화 상태 확인 함수 (회사 설정 AND 관리타입)
export const isGameEnabled = (gameType, companyCode = null) => {
  if (!isManageGameAllowed(gameType)) return false;
  const config = getMenuConfig(companyCode);
  return config.games[gameType] || false;
};

/**
 * 기능(정산, 베팅 관리 등) 활성화 여부 — default.js 의 features 기준
 * 회사별 설정에서 features.settlement 등을 false 로 두면 메뉴에서 제외
 */
export const isFeatureEnabled = (featureKey, companyCode = null) => {
  const config = getMenuConfig(companyCode);
  const features = config.features;
  if (!features || typeof features[featureKey] === 'undefined') {
    return true;
  }
  return !!features[featureKey];
};

/**
 * 개발자용 설정 업데이트 함수
 * 런타임에서 설정을 변경할 때 사용합니다.
 * 주의: 프로덕션 환경에서는 사용하지 마세요.
 * @param {Object} updates - 업데이트할 설정
 * @param {string|number} companyCode - 회사코드 (없으면 현재 사용자의 회사코드 사용)
 */
export const updateMenuConfig = (updates, companyCode = null) => {
  if (process.env.NODE_ENV === 'development') {
    let code = companyCode;
    if (!code) {
      code = getCurrentCompanyCode();
    } else {
      code = String(code).padStart(4, '0');
    }
    
    if (code) {
      // 런타임 업데이트는 COMPANY_CONFIGS에 직접 추가
      if (!COMPANY_CONFIGS[code]) {
        COMPANY_CONFIGS[code] = {};
      }
      const mergedConfig = deepMerge(getMenuConfig(code), updates);
      COMPANY_CONFIGS[code] = mergedConfig;
      console.warn(`회사코드 ${code}의 메뉴 설정이 런타임에서 변경되었습니다.`);
    } else {
      console.warn('회사코드를 찾을 수 없어 메뉴 설정을 업데이트할 수 없습니다.');
    }
  }
};

/**
 * 개발자용 설정 초기화 함수
 * 기본 설정으로 초기화합니다.
 * 주의: 프로덕션 환경에서는 사용하지 마세요.
 * @param {string|number} companyCode - 회사코드 (없으면 현재 사용자의 회사코드 사용)
 */
export const resetMenuConfig = (companyCode = null) => {
  if (process.env.NODE_ENV === 'development') {
    let code = companyCode;
    if (!code) {
      code = getCurrentCompanyCode();
    } else {
      code = String(code).padStart(4, '0');
    }
    
    if (code && COMPANY_CONFIGS[code]) {
      delete COMPANY_CONFIGS[code];
      console.warn(`회사코드 ${code}의 메뉴 설정이 기본값으로 초기화되었습니다.`);
    } else {
      console.warn('회사코드를 찾을 수 없거나 설정이 없습니다.');
    }
  }
};

